import { useLayoutEffect, useRef, useState } from "react";
import { EyeIcon, EyeOffIcon, LockIcon } from "./icons";
import styles from "./Admin.module.css";

export function Field({ label, hint, textarea, ...rest }) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      {textarea ? <textarea rows={4} {...rest} /> : <input {...rest} />}
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function IconField({ label, icon, ...rest }) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      <div className={styles.passwordWrap}>
        <i className={styles.leadIcon}>{icon}</i>
        <input {...rest} className={styles.withIcon} />
      </div>
    </label>
  );
}

// Browsers always draw dots for type="password", so the field is a text input
// that displays "*" per character while the real value stays in `value`.
export function PasswordField({ label, value, onChange, ...rest }) {
  const [show, setShow] = useState(false);
  const inputRef = useRef(null);
  const sel = useRef({ start: 0, end: 0 });
  const caret = useRef(null);

  useLayoutEffect(() => {
    if (caret.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  });

  const remember = (e) => {
    sel.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
  };

  const handleChange = (e) => {
    const typed = e.target.value;
    if (show) {
      onChange({ target: { value: typed } });
      return;
    }
    const pos = e.target.selectionStart;
    const { start, end } = sel.current;
    const inserted = typed.length - (value.length - (end - start));
    let next;
    if (inserted > 0) {
      next = value.slice(0, start) + typed.slice(start, start + inserted) + value.slice(end);
    } else if (end > start) {
      next = value.slice(0, start) + value.slice(end);
    } else {
      const removed = value.length - typed.length;
      next = value.slice(0, pos) + value.slice(pos + removed);
    }
    caret.current = pos;
    onChange({ target: { value: next } });
  };

  return (
    <label className={styles.field}>
      <span>{label}</span>
      <div className={styles.passwordWrap}>
        <i className={styles.leadIcon}><LockIcon /></i>
        <input
          {...rest}
          ref={inputRef}
          className={styles.withIcon}
          type="text"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          value={show ? value : "*".repeat(value.length)}
          onChange={handleChange}
          onSelect={remember}
          onKeyDown={remember}
          onMouseUp={remember}
        />
        <button
          type="button"
          className={styles.eyeBtn}
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </label>
  );
}

export function SelectField({ label, hint, children, ...rest }) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      <select {...rest}>{children}</select>
      {hint && <small>{hint}</small>}
    </label>
  );
}

export function PanelMessage({ children }) {
  return <div className={styles.panelMsg}>{children}</div>;
}

export function Spinner() {
  return <div className={styles.spinner} aria-label="Loading" />;
}

export function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function friendlyError(err) {
  // Surface the raw error so setup problems are diagnosable from the console.
  console.error("Admin action failed:", err);

  const code = err?.code || "";
  const map = {
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/weak-password": "Password is too weak (min 6 characters).",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/email-already-in-use": "That email already has a login. Please sign in instead.",
    "permission-denied": "You don't have permission to do that. Check the Firestore rules.",
    validation: "Please fill in all required fields.",
    unauthenticated: "Your session has expired. Please sign in again.",
    unauthorized: "Your session has expired. Please sign in again.",
    forbidden: "Only admins can do that.",
    invalid_credentials: "Incorrect email or password.",
    invalid_email: "Please enter a valid email address.",
    weak_password: "Password must be at least 6 characters.",
    account_disabled: "This login has been disabled. Ask an admin to re-enable it.",
    email_taken: "A team member with that email already exists.",
    self_change: "You can't change your own role or disable your own login.",
    self_delete: "You can't remove your own login.",
    last_admin: "At least one active admin is required.",
    setup_already_complete: "Setup has already been completed. Please sign in.",
    already_setup: "Setup has already been completed. Please sign in.",
    invalid_invite: "This invite link is invalid or has expired. Ask an admin to send a new one.",
    mail_failed: "The invite was saved, but the email could not be sent. Check the server's mail setup.",
    invalid_otp: "That code is incorrect or has expired.",
    too_many_requests: "Please wait a minute before requesting another code.",
    network: "Couldn't reach the server. Check your connection and try again.",
    server_error: "The server hit an unexpected error. Please try again.",
  };
  if (map[code]) return map[code];
  const detail = code || err?.message || "unknown error";
  return `Something went wrong (${detail}). Please try again.`;
}
